import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Stack,
    Box,
    Alert,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useForgotPassword } from '../../../hooks/useAuth';
import { useToastContext } from '../../../context/ToastContext';
import { usePostHog } from '@posthog/react';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const navigate = useNavigate();
    const { addToast } = useToastContext();
    const forgotPasswordMutation = useForgotPassword();
    const posthog = usePostHog();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email.trim()) {
            addToast('Please enter your email address', 'error');
            return;
        }

        try {
            await forgotPasswordMutation.mutateAsync(email);
            posthog?.capture('forgot_password_requested');
            setShowSuccess(true);
            addToast('Password reset email sent!', 'success');
        } catch (error) {
            console.error('Forgot password error:', error);
            addToast('Failed to send reset email. Please try again.', 'error');
        }
    };

    if (showSuccess) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="80vh"
                px={2}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card sx={{ maxWidth: 500, width: '100%' }}>
                        <CardContent>
                            <Typography variant="h5" gutterBottom align="center">
                                Check Your Email
                            </Typography>
                            
                            <Alert severity="success" sx={{ mt: 2, mb: 3 }}>
                                If an account exists with that email, a password reset link has been sent.
                            </Alert>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                Please check your email inbox (and spam folder) for a password reset link.
                                The link will expire in 1 hour.
                            </Typography>

                            <Stack spacing={2} mt={3}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/')}
                                    startIcon={<ArrowLeft />}
                                >
                                    Back to Login
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </motion.div>
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="80vh"
            px={2}
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Card sx={{ maxWidth: 500, width: '100%' }}>
                    <CardContent>
                        <Typography variant="h5" gutterBottom align="center">
                            Forgot Password
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" paragraph align="center">
                            Enter your email address and we'll send you a link to reset your password.
                        </Typography>

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={2} mt={3}>
                                <TextField
                                    type="email"
                                    label="Email Address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    fullWidth
                                    required
                                    autoFocus
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    disabled={forgotPasswordMutation.isPending}
                                >
                                    {forgotPasswordMutation.isPending
                                        ? 'Sending...'
                                        : 'Send Reset Link'}
                                </Button>

                                <Button
                                    variant="text"
                                    onClick={() => navigate('/')}
                                    startIcon={<ArrowLeft />}
                                >
                                    Back to Login
                                </Button>
                            </Stack>
                        </form>
                    </CardContent>
                </Card>
            </motion.div>
        </Box>
    );
}